import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleExclamation } from "@fortawesome/free-solid-svg-icons";

const STATUS_STYLES = {
  blue: {
    border: "border-sky-300",
    header: "bg-sky-50 border-sky-100",
    badge: "bg-sky-100 text-sky-800",
    input: "focus:ring-sky-300/40",
  },
  green: {
    border: "border-emerald-300",
    header: "bg-emerald-50 border-emerald-100",
    badge: "bg-emerald-100 text-emerald-800",
    input: "focus:ring-emerald-300/40",
  },
  amber: {
    border: "border-amber-300",
    header: "bg-amber-50 border-amber-100",
    badge: "bg-amber-100 text-amber-800",
    input: "focus:ring-amber-300/40",
  },
  red: {
    border: "border-red-300",
    header: "bg-red-50 border-red-100",
    badge: "bg-red-100 text-red-800",
    input: "focus:ring-red-300/40",
  },
};

const ClinicalInputCard = ({
  title,
  description,
  value,
  onChange,
  required = false,
  placeholder = "",
  link,
  linkLabel,
  status,
  statusLabel,
  statusDescription,
}) => {
  const activeStyles = status ? STATUS_STYLES[status] : null;
  const isNotCarriedOut =
    String(value || "")
      .trim()
      .toLowerCase() === "n/a";

  const handleNotCarriedOutToggle = (e) => {
    const nextNotCarriedOut = e.target.checked;
    onChange({
      target: {
        value: nextNotCarriedOut ? "n/a" : "",
      },
    });
  };

  return (
    <div
      className={`rounded-2xl bg-white shadow-sm overflow-hidden ${
        activeStyles?.border || "border border-sky-200"
      }`}
    >
      <div
        className={`px-4 py-3 border-b ${activeStyles?.header || "border-sky-100 bg-sky-50"}`}
      >
        <div className="flex items-center justify-between gap-3">
          <h2 className="font-fatfrank text-lg md:text-xl text-sas-primary">
            {title}
          </h2>
          <div className="flex items-center gap-3">
            {status ? (
              <span
                className={`inline-flex shrink-0 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-[0.2em] ${
                  activeStyles?.badge || "bg-slate-100 text-slate-700"
                }`}
              >
                {statusLabel || status}
              </span>
            ) : null}
            <label className="inline-flex items-center gap-2 cursor-pointer select-none">
              <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-sas-primary">
                Not carried out
              </span>
              <span className="relative inline-flex h-5 w-10 items-center">
                <input
                  type="checkbox"
                  className="peer sr-only"
                  checked={isNotCarriedOut}
                  onChange={handleNotCarriedOutToggle}
                />
                <span className="absolute inset-0 rounded-full bg-slate-300 transition peer-checked:bg-sas-primary"></span>
                <span className="absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white transition peer-checked:translate-x-5"></span>
              </span>
            </label>
          </div>
        </div>
      </div>
      <div className="p-4 flex flex-col gap-3 md:flex-row md:items-stretch">
        <p className="text-sas-primary-dark text-sm md:text-base leading-relaxed md:flex-1 bg-sas-primary-hover/70 rounded-lg p-3">
          {description}
        </p>
        <div className="w-full md:w-56">
          <input
            type="text"
            className={`w-full rounded-lg border border-slate-300 px-4 py-3 text-base font-semibold text-sas-primary-dark focus:outline-none focus:ring-2 disabled:bg-slate-100 disabled:text-slate-500 disabled:cursor-not-allowed ${
              activeStyles?.input || "focus:ring-sas-primary/30"
            }`}
            value={value}
            onChange={onChange}
            required={required && !isNotCarriedOut}
            disabled={isNotCarriedOut}
            placeholder={placeholder}
          />
        </div>
      </div>
      {statusDescription ? (
        <div className="px-4 pb-2">
          <p className="text-xs text-slate-500 font-montserrat">
            {statusDescription}
          </p>
        </div>
      ) : null}
      {link && linkLabel ? (
        <a
          target="_blank"
          rel="noreferrer"
          href={link}
          className="inline-flex items-center px-4 pb-4 text-sas-primary-dark"
        >
          <FontAwesomeIcon
            icon={faCircleExclamation}
            className="text-sas-primary"
          />
          <span className="pl-2 text-xs md:text-sm font-montserrat">
            {linkLabel}
          </span>
        </a>
      ) : null}
    </div>
  );
};

export default ClinicalInputCard;
